import { ___, getLocale } from '@ts/common/language';
export class HttpError extends Error {
    status;
    statusText;
    data;
    constructor(status, statusText, data) {
        super(`HTTP Error: ${status} ${statusText}`);
        this.status = status;
        this.statusText = statusText;
        this.data = data;
        this.name = 'HttpError';
    }
}
export function isObject(data) {
    return typeof data === 'object' && !Array.isArray(data);
}
export function isArray(data) {
    return Array.isArray(data);
}
export function isError(error) {
    return error instanceof Error;
}
// Internal — used by the four sibling type-guards below.
function isHttpError(error) {
    return error instanceof HttpError;
}
export function isAbortError(error) {
    return error instanceof Error && error.name === 'AbortError';
}
export function isHttpErrorWithStatus(error, status) {
    return isHttpError(error) && error.status === status;
}
export function isHttpHttpErrorWithObjectData(error) {
    return isHttpError(error) && typeof error.data === 'object' && !Array.isArray(error.data);
}
export function isHttpErrorWithValidationData(error) {
    // Leading null/undefined guard — a type-guard must not throw on plausible
    // input (callers' `catch (e)` may bind `null` / `undefined` if a non-Error
    // value was thrown).
    return error != null && error.status === 422 && isHttpHttpErrorWithObjectData(error) && 'message' in error.data && 'errors' in error.data && typeof error.data.message === 'string' && typeof error.data.errors === 'object';
}
/**
 * Our own wording for the 4xx statuses whose response body carries framework English
 * ("Too Many Attempts.", "CSRF token mismatch.") rather than anything we authored.
 *
 * These are raised by middleware that runs BEFORE `setlocaleifisset` — `throttle:api`
 * sits in the Kernel's `api` group, ahead of the route-level locale middleware — so the
 * server has not resolved a locale yet and cannot translate them. The client always
 * knows its own language, so it answers for them here.
 */
const STATUS_MESSAGES = {
    401: {
        en: 'You are not logged in. Please log in and try again.',
        de: 'Du bist nicht eingeloggt. Bitte melde dich an und versuche es erneut.',
    },
    403: {
        en: 'You are not allowed to do this.',
        de: 'Du darfst diese Aktion nicht ausführen.',
    },
    404: {
        en: 'This item no longer exists. Please reload the page.',
        de: 'Dieses Element existiert nicht mehr. Bitte lade die Seite neu.',
    },
    413: {
        en: 'The file is too large.',
        de: 'Die Datei ist zu groß.',
    },
    419: {
        en: 'Your session has expired. Please reload the page and try again.',
        de: 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu und versuche es erneut.',
    },
    429: {
        en: 'Too many attempts in a short time. Please wait a moment and try again.',
        de: 'Zu viele Versuche in kurzer Zeit. Bitte warte einen Moment und versuche es erneut.',
    },
};
function statusMessage(status) {
    const entry = STATUS_MESSAGES[status];
    if (entry === undefined)
        return undefined;
    // `___` yields '' — not undefined — when setLanguage() has not run (node tests, Web
    // Workers, the PDF pipeline). Map that back so the caller's own fallback still wins.
    const text = ___(entry);
    return text !== '' ? text : undefined;
}
/**
 * The human-readable reason a request failed, in the user's language.
 *
 * One rule: 422 is the only status whose body we author, so it is the only one whose
 * words we repeat. A 422 is localized server-side twice over — `abort(422, ___(…))` via
 * the PHP helper, and `validate()` via resources/lang/<locale>/validation.php with the
 * localized attribute names the controllers pass — because `setlocaleifisset` has
 * applied the `?locale=` before the controller runs. Every other 4xx is answered from
 * STATUS_MESSAGES instead, since its body is untranslated framework text.
 *
 * Restricted to 4xx throughout: 5xx bodies carry framework/debug text that must not be
 * put in front of a user. Returns `undefined` when there is nothing worth showing, so
 * callers keep their own generic fallback:
 *
 *   alert.value = serverErrorMessage(error) ?? ___({en: '…', de: '…'});
 */
export function serverErrorMessage(error) {
    if (!isHttpError(error) || error.status < 400 || error.status >= 500) {
        return undefined;
    }
    if (error.status !== 422) {
        return statusMessage(error.status);
    }
    if (isHttpErrorWithValidationData(error)) {
        const firstField = Object.values(error.data.errors).find((messages) => Array.isArray(messages) && messages.length > 0);
        if (firstField !== undefined) {
            return firstField[0];
        }
    }
    // `data` is `object`-typed here, but `typeof null === 'object'` — see isObject().
    if (!isHttpHttpErrorWithObjectData(error) || error.data === null) {
        return undefined;
    }
    const message = error.data.message;
    return typeof message === 'string' && message !== '' ? message : undefined;
}
export async function fetchData(url, options, timeout) {
    const controller = timeout !== undefined ? new AbortController() : undefined;
    let timeoutId = undefined;
    if (controller) {
        timeoutId = setTimeout(() => controller.abort(), timeout);
    }
    try {
        const response = await fetch(url, {
            ...(controller ? { signal: controller.signal } : {}),
            ...options,
        });
        // console.log('fetchData - response', response);
        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        let responseData;
        try {
            if (contentType.includes("application/json")) {
                // console.log('fetchData - contentType is application/json, treating as JSON');
                responseData = await response.json();
            }
            else if (contentType.includes("application/octet-stream")) {
                // console.log('fetchData - contentType is application/octet-stream, treating as blob');
                responseData = await response.blob();
            }
            else if (contentType.includes("text/")) {
                const text = await response.text();
                try {
                    responseData = JSON.parse(text);
                }
                catch {
                    responseData = text;
                }
            }
            else {
                const blob = await response.blob();
                try {
                    const text = await blob.text();
                    try {
                        responseData = JSON.parse(text);
                    }
                    catch {
                        responseData = blob;
                    }
                }
                catch {
                    responseData = blob;
                }
            }
        }
        catch {
            responseData = null;
        }
        if (!response.ok) {
            if (response.status === 419) {
                // Try to fetch new CSRF token and retry
                try {
                    const csrfUrl = `/api/v1/keep-alive?locale=${getLocale()}`;
                    const csrfResponse = await fetch(csrfUrl, {
                        method: 'GET',
                        headers: { Accept: 'application/json' },
                    });
                    if (csrfResponse.ok) {
                        const csrfData = await csrfResponse.json();
                        if (csrfData && csrfData.csrf) {
                            if (typeof window !== 'undefined')
                                window.csrf = csrfData.csrf;
                            const metaTag = document.querySelector('meta[name="csrf-token"]');
                            if (metaTag)
                                metaTag.setAttribute('content', csrfData.csrf);
                            // Retry the original request with new token
                            const newOptions = { ...options, _retried: true };
                            if (options && options._retried) {
                                throw new HttpError(response.status, response.statusText, responseData);
                            }
                            if (!newOptions.headers) {
                                newOptions.headers = {};
                            }
                            if (newOptions.headers instanceof Headers) {
                                newOptions.headers.set('X-CSRF-TOKEN', csrfData.csrf);
                            }
                            else if (Array.isArray(newOptions.headers)) {
                                const idx = newOptions.headers.findIndex(h => h[0].toLowerCase() === 'x-csrf-token');
                                if (idx >= 0)
                                    newOptions.headers[idx][1] = csrfData.csrf;
                                else
                                    newOptions.headers.push(['X-CSRF-TOKEN', csrfData.csrf]);
                            }
                            else {
                                newOptions.headers['X-CSRF-TOKEN'] = csrfData.csrf;
                            }
                            return await fetchData(url, newOptions, timeout);
                        }
                    }
                }
                catch (e) {
                    // Fall through back to original error
                }
            }
            throw new HttpError(response.status, response.statusText, responseData);
        }
        return responseData;
    }
    catch (error) {
        if (error instanceof HttpError) {
            throw error;
        }
        if (error instanceof Error) {
            throw error;
        }
        else {
            throw new Error('Unknown error');
        }
    }
    finally {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
    }
}
